import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ClienteResumo } from '@/data/dashboardMockData';
import { cn } from '@/lib/utils';

interface ClientListModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  clients: ClienteResumo[];
  type: 'open' | 'overdue';
}

function formatMoney(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function ClientListModal({ isOpen, onClose, title, clients, type }: ClientListModalProps) {
  const totalValor = clients.reduce((sum, c) => sum + c.valor, 0);
  const totalFaturas = clients.reduce((sum, c) => sum + c.faturas, 0);

  const headerBg = type === 'open' 
    ? 'bg-[hsl(var(--basic-50))]' 
    : 'bg-[hsl(var(--careful-50))]';

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader className={cn('p-4 -m-6 mb-4 rounded-t-lg', headerBg)}>
          <DialogTitle className="text-lg font-semibold text-foreground">
            Clientes - {title}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            {clients.length} cliente{clients.length !== 1 ? 's' : ''} • {totalFaturas} fatura{totalFaturas !== 1 ? 's' : ''}
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-auto -mx-6 px-6">
          {clients.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              Nenhum cliente encontrado neste período.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-background">
                <tr className="border-b border-border">
                  <th className="text-left py-2 px-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Nome
                  </th>
                  <th className="text-left py-2 px-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    CNPJ/CPF
                  </th>
                  <th className="text-center py-2 px-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Faturas
                  </th>
                  <th className="text-right py-2 px-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Valor
                  </th>
                </tr>
              </thead>
              <tbody>
                {clients.map((cliente, index) => (
                  <tr
                    key={cliente.id}
                    className={cn(
                      'border-b border-border/50 hover:bg-muted/30 transition-colors cursor-pointer',
                      index === clients.length - 1 && 'border-b-0'
                    )}
                  >
                    <td className="py-3 px-2">
                      <span className="font-medium text-foreground">{cliente.nome}</span>
                    </td>
                    <td className="py-3 px-2">
                      <span className="text-muted-foreground text-xs">{cliente.cnpj}</span>
                    </td>
                    <td className="py-3 px-2 text-center">
                      <span className="text-foreground">{cliente.faturas}</span>
                    </td>
                    <td className="py-3 px-2 text-right">
                      <span className={cn(
                        'font-semibold',
                        type === 'overdue' ? 'text-[hsl(var(--careful-600))]' : 'text-foreground'
                      )}>
                        {formatMoney(cliente.valor)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer com totais */}
        <div className={cn(
          'border-t border-border pt-4 mt-4 -mx-6 px-6 -mb-6 pb-6',
          headerBg
        )}>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-foreground">
              Total: {clients.length} cliente{clients.length !== 1 ? 's' : ''}
            </span>
            <span className={cn(
              'text-lg font-bold',
              type === 'overdue' ? 'text-[hsl(var(--careful-600))]' : 'text-[hsl(var(--basic-600))]'
            )}>
              {formatMoney(totalValor)}
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
